// src/components/Header.tsx
import React, { useState, useLayoutEffect, useEffect } from "react";
import styled, { css } from "styled-components";
import { useDispatch } from "react-redux";
import { handlePageSelection } from "../features/pageSlice";
import { FaBars, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../app/store";
import UserHeaderButton from "./UserHeaderButton";
import LanguageSelector from "./LanguageSelector";
import localization from "../services/localization";
import { showToast } from "../utils/utility";
import { ModalType, useModal } from "keycloakauthmodule";

const HeaderWrapper = styled.div`
  height: 64px;
  padding: 0 32px;
  box-shadow: inset 0 -2px 2px 0 rgba(0, 170, 255, 0.4),
    inset 0 2px 2px 0 rgba(13, 21, 26, 0.6);
  background-color: #000d0c;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  position: sticky;
  top: 0;
  z-index: 1000;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;

  cursor: pointer;
  user-select: none; /* Prevent text selection when clicking */
  transition: transform 0.2s ease; /* Add a smooth transform transition */

  &:hover {
    transform: scale(1.1); /* Scale up on hover */
  }
`;

const MenuButton = styled.button`
  background-color: transparent;
  border: none;
  color: rgba(0, 170, 255, 0.4);
  cursor: pointer;
  font-size: 24px; /* Adjust the font size as needed */
  display: flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;
const LogoImage = styled.img`
  width: 64px;
  height: 64px;
  margin-right: 16px;

  @media (max-width: 600px) {
    width: 48px;
    height: 48px;
    margin-right: 8px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertical centering */
  height: 100%; /* Ensure the container takes full height */
  font-family: 'BradleyHandBold', sans-serif; /* Use BradleyHandBold */
  font-size: 24px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #fff;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const HeaderCenter = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

interface HeaderButtonProps {
  disabled?: boolean;
  onClick?: () => void;
}

const HeaderButton = styled.button<HeaderButtonProps>`
  background-color: transparent;
  border: none;
  color: ${({ disabled }) => (disabled ? "#808080" : "white")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin: 0 16px;
  height: 19px;
  font-family: 'Corbel', sans-serif; /* Use Corbel font */
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;

  &:hover {
    ${({ disabled }) =>
    !disabled &&
    css`
        text-decoration: underline;
      `}
  }
`;

const SignUpButton = styled.button`
  width: 120px;
  height: 40px;
  margin: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 
    inset 0 2px 2px rgba(255, 255, 255, 0.3),
    inset 0 -2px 2px rgba(0, 0, 0, 0.3),
    0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #00a4b2;
  border: none;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  font-family: 'Corbel', sans-serif; /* Use Corbel font */

  &:hover {
    background-color: #008392;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const LoginButton = styled.button`
  width: 120px;
  height: 40px;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 2px solid #b2fff9;
  background-color: transparent;
  color: #fff2b2;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'Corbel', sans-serif; /* Use Corbel font */
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: rgba(178, 255, 249, 0.1);
    color: #ffe48c;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(1px);
  }
`;


const SlidingBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #00171a;
  z-index: 1000;
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;

  &.show {
    transform: translateX(0);
  }
`;

const SlidingBarContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: top;
  height: 100%;
  margin-top: 16px;
`;

const SlidingBarButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 24px;
  background-color: transparent;
  border: none;
  color: #fff;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

interface SlidingBarItemProps {
  isselected: string; // Change the type to string
}

const SlidingBarItem = styled.button<SlidingBarItemProps>`
  width: 80%;
  background-color: transparent;
  border: none;
  color: #fff;
  cursor: ${({ isselected }) => (isselected === "true" ? "default" : "pointer")};
  margin: 16px 0;
  font-family: 'Corbel', sans-serif; /* Use Corbel font */
  font-size: 18px;
  position: relative;
  padding: 8px 0;
  transition: background-color 0.3s;
  border-bottom: 1px solid #26494d;

  &:hover {
    ${({ isselected }) =>
    isselected !== "true" &&
    css`
        text-decoration: underline;
      `}
  }

  ${(props) =>
    props.isselected === "true"
      ? css`
          background-color: #00a4b2;
          color: #000;
          &:after {
            content: "";
            width: calc(100% - 16px);
            height: 2px;
            background-color: var(--primary-light-blue);
            position: absolute;
            bottom: 0;
            left: 8px;
          }
        `
      : css`
          &:after {
            content: "";
            width: 0;
            height: 2px;
            background-color: transparent;
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
          }
        `}

  ${(props) =>
    props.isselected === "true" &&
    css`
      cursor: default;
      &:hover {
        text-decoration: none;
      }
    `}
  
  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.5;
      &:hover {
        text-decoration: none;
      }
    `}
`;

const Header: React.FC = () => {
  const dispatch: AppDispatch = useDispatch(); // Use AppDispatch
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [showSlidingBar, setShowSlidingBar] = useState(false);
  const navigate = useNavigate();
  const { openModal } = useModal();
  const location = useLocation();

  const selectedPage = useSelector(
    (state: RootState) => state.page.selectedPage
  );

  const signupClick = () => {
    openModal(ModalType.SIGN_UP);
  };

  const signinClick = () => {
    openModal(ModalType.SIGN_IN, { returnTo:"/" });
  };

  const handlePageClick = (page: string) => {
    dispatch(handlePageSelection(page));
    setShowSlidingBar(false); // Close the sliding bar when a page is selected
    navigate(`/${page}/`);
  };

  const toggleSlidingBar = () => {
    setShowSlidingBar(!showSlidingBar);
  };

  useLayoutEffect(() => {
    setWindowWidth(window.innerWidth);
  }, []); // Calculate initial width without waiting for a render

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
  const path = location.pathname.toLowerCase();

  if (path.startsWith("/profile")) {
    dispatch(handlePageSelection("profile"));
  } else if (path.startsWith("/account")) {
    dispatch(handlePageSelection("account"));
  } else if (path.startsWith("/transactions")) {
    dispatch(handlePageSelection("transactions"));
  } else if (path.startsWith("/history")) {
    dispatch(handlePageSelection("history"));
  } else {
    const first = path.split("/")[1] || "home";
    dispatch(handlePageSelection(first));
  }
}, [location.pathname, dispatch]);


  const TOKEN = localStorage.getItem("token");


  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const TOKEN = localStorage.getItem('token'); // Retrieve token dynamically

      if (!TOKEN) {
        // Only proceed if token is null
        if (hash === "#signup") {
          signupClick(); // Trigger signup click action
        } else if (hash === "#signin") {
          signinClick(); // Trigger signin click action
        }
      } else {
        // Show toast for logged-in users
        if (hash === "#signup") {
          showToast("You are already logged in. Signup is not required.", {
            autoClose: 3000,
            type: "info",
          });
        } else if (hash === "#signin") {
          showToast("You are already logged in. Signin is not required.", {
            autoClose: 3000,
            type: "info",
          });
        }
      }
    };

    // Check hash on component mount
    handleHashChange();

    // Add event listener for hash change
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      // Cleanup event listener on component unmount
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []); // Dependency array remains empty since TOKEN is read dynamically


  const handleHeaderClick = () => {
    dispatch(handlePageSelection("home"));
    setShowSlidingBar(false); // Close the sliding bar when a page is selected
    navigate(`/`);
  };


  const logoutUser = () => {
    localStorage.clear();
    window.location.href = "/";
    sessionStorage.clear(); // This will clear all sessionStorage keys
    handleHeaderClick();
  };

  return (
    <>
      <HeaderWrapper>
        {windowWidth < 768 ? (
          // Show menu button on leftHeader, logo and text on centerHeader, empty rightHeader
          <>
            <HeaderLeft onClick={handleHeaderClick}>
              <LogoImage src="/logo.png" alt="Logo" />
              <HeaderText>BOXCHAOS</HeaderText>
            </HeaderLeft>
            <HeaderRight>
              <LanguageSelector />

              <MenuButton onClick={toggleSlidingBar}>
                <FaBars />
              </MenuButton>
            </HeaderRight>
          </>
        ) : (
          // Show logo and text on leftHeader, buttons on centerHeader, signup and login on rightHeader
          <>
            <HeaderLeft onClick={handleHeaderClick}>
              <LogoImage src="/logo.png" alt={localization.logo} />
              <HeaderText>BOXCHAOS</HeaderText>
            </HeaderLeft>
            <HeaderCenter>
              <HeaderButton onClick={() => handlePageClick("boxes")}>
                {localization.all_boxes}
              </HeaderButton>
              <HeaderButton
                onClick={() => (TOKEN ? handlePageClick("chaos") : undefined)}
                disabled={!TOKEN}
              >
                {localization.chaos}
              </HeaderButton>
              <HeaderButton
                onClick={() => (TOKEN ? handlePageClick("gifts") : undefined)}
                disabled={!TOKEN}
              >
                {localization.gifts}
              </HeaderButton>
              <HeaderButton
                onClick={() => handlePageClick("vips")}
                disabled={!TOKEN}
              >
                {localization.vips}
              </HeaderButton>
              <HeaderButton
                onClick={() => handlePageClick("all_promotions")}
              >
                {localization.promotions}
              </HeaderButton>
            </HeaderCenter>
            <HeaderRight>
              {TOKEN ? (
                <>
                  <UserHeaderButton onPageClick={handlePageClick} />
                  <LanguageSelector />

                </>
              ) : (
                <>
                  <LanguageSelector />

                  <SignUpButton onClick={() => signupClick()}>
                    {localization.signup}
                  </SignUpButton>
                  <LoginButton onClick={() => signinClick()}>
                    {localization.login}
                  </LoginButton>
                </>
              )}
            </HeaderRight>
          </>
        )}
      </HeaderWrapper>
      {windowWidth < 768 && (
        <SlidingBar className={showSlidingBar ? "show" : ""}>
          <HeaderWrapper>
            <HeaderLeft>
              <LogoImage src="/logo.png" alt={localization.logo} />
              <HeaderText>BOXCHAOS</HeaderText>
            </HeaderLeft>
          </HeaderWrapper>

          <SlidingBarButton onClick={toggleSlidingBar}>
            <FaTimes />
          </SlidingBarButton>

          <SlidingBarContent>
            {/* Add menu items or links for the sliding bar */}

            {TOKEN ? (
              <>
                <SlidingBarItem
                  isselected={String(selectedPage === "profile")}
                  onClick={() => handlePageClick("profile")}
                >
                  {localization.profile_settings}
                </SlidingBarItem>
                <SlidingBarItem
                  isselected={String(selectedPage === "account")}
                  onClick={() => handlePageClick("account")}
                >
                  {localization.account}
                </SlidingBarItem>
                <SlidingBarItem
                  isselected={String(selectedPage === "transactions")}
                  onClick={() => handlePageClick("transactions")}
                >
                  {localization.transactions}
                </SlidingBarItem>
                <SlidingBarItem
                  isselected={String(selectedPage === "history")}
                  onClick={() => handlePageClick("history")}
                >
                  {localization.history}
                </SlidingBarItem>
              </>
            ) : (
              <>
                <SlidingBarItem
                  isselected={String(selectedPage === "signup")}
                  onClick={() => signupClick()}
                >
                  {localization.signup}
                </SlidingBarItem>
                <SlidingBarItem
                  isselected={String(selectedPage === "login")}
                  onClick={() => signinClick()}
                >
                  {localization.login}
                </SlidingBarItem>
              </>
            )}

            <SlidingBarItem
              isselected={String(selectedPage === "boxes")}
              onClick={() => handlePageClick("boxes")}
            >
              {localization.all_boxes}
            </SlidingBarItem>
            <SlidingBarItem
              isselected={String(selectedPage === "chaos")}
              onClick={TOKEN ? () => handlePageClick("chaos") : () => { }}
              disabled={!TOKEN}
            >
              {localization.chaos}
            </SlidingBarItem>
            <SlidingBarItem
              isselected={String(selectedPage === "gifts")}
              onClick={TOKEN ? () => handlePageClick("gifts") : () => { }}
              disabled={!TOKEN}
            >
              {localization.gifts}
            </SlidingBarItem>
            <SlidingBarItem
              isselected={String(selectedPage === "vips")}
              onClick={TOKEN ? () => handlePageClick("vips") : () => { }}
              disabled={!TOKEN}
            >
              {localization.vips}
            </SlidingBarItem>
                        <SlidingBarItem
              isselected={String(selectedPage === "all_promotions")}
              onClick={ () => handlePageClick("all_promotions")}
            >
              {localization.promotions}
            </SlidingBarItem>

            {TOKEN ? (
              <SlidingBarItem
                isselected={String(selectedPage === "logout")}
                onClick={logoutUser}
              >
                {localization.logout}
              </SlidingBarItem>
            ) : (
              <></>
            )}
            {/* Add more items for other pages */}
          </SlidingBarContent>
        </SlidingBar>
      )}
    </>
  );
};

export default Header;
